import React, { useState } from 'react'
import ChimeProvider from './chime-provider'

export type DeviceType = {
  deviceId: string
  label: string
}

interface AudioCaptureContextType {
  isReady: boolean
  audioInputs: DeviceType[]
  selectedAudioInput: string
  setSelectedAudioInput: (deviceId: string) => void
  testAudioInputs: () => Promise<void>
  hasMicAccess: boolean
  setHasMicAccess: React.Dispatch<React.SetStateAction<boolean>>
  promptForDevicePermissions: () => void
  isMuted: boolean
  toggleMute: () => void
  disableEchoCancelation: React.Dispatch<React.SetStateAction<boolean>>
  isRecording: boolean
  isRecordingLoading: boolean
  startRecording: ({
    isTelehealth,
    isUsingHeadphones,
    noteType
  }: {
    isTelehealth: boolean
    isUsingHeadphones: boolean
    noteType: string
  }) => Promise<void>
  stopRecording: () => Promise<void>
  startContentShare: () => Promise<void>
  stopContentShare: () => void
  logData: object
}

export const AudioCaptureContext = React.createContext<
  AudioCaptureContextType | undefined
>(undefined)

interface AudioCaptureProviderProps {
  isChime: boolean
  sessionId: string
  hasStartedRecording: boolean
  refetchRecap: () => void
  children: React.ReactNode
}

export const AudioCaptureProvider: React.FC<AudioCaptureProviderProps> = ({
  isChime,
  sessionId,
  refetchRecap,
  hasStartedRecording,
  children
}) => {
  const [isRecording, _setIsRecording] = useState(false)
  const [hasMicAccess, setHasMicAccess] = useState(true)
  const [_disableEchoCancelation, setDisableEchoCancelation] = useState(false)
  const [selectedDeviceId, setSelectedDeviceId] = useState('')

  const manualValue = {
    isReady: true,
    audioInputs: [],
    selectedAudioInput: selectedDeviceId,
    setSelectedAudioInput: setSelectedDeviceId,
    testAudioInputs: () => Promise.resolve(),
    hasMicAccess,
    setHasMicAccess,
    promptForDevicePermissions: () => {},
    isMuted: false,
    toggleMute: () => {},
    disableEchoCancelation: setDisableEchoCancelation,
    isRecording,
    isRecordingLoading: false,
    startRecording: () => Promise.resolve(),
    stopRecording: () => Promise.resolve(),
    startContentShare: () => Promise.resolve(),
    stopContentShare: () => {},
    logData: {}
  }

  return isChime ? (
    <ChimeProvider
      sessionId={sessionId}
      hasStartedRecording={hasStartedRecording}
      refetchRecap={refetchRecap}
    >
      {children}
    </ChimeProvider>
  ) : (
    <AudioCaptureContext.Provider value={manualValue}>
      {children}
    </AudioCaptureContext.Provider>
  )
}

export default AudioCaptureProvider
